import { useEffect, useRef, useState, useMemo, useLayoutEffect, useCallback } from "react";
import { useKpiCardFontSize } from "./KpiCardFontContext";

// Najmenšia prípustná veľkosť sumy na karte (px)
const MIN_FONT_SIZE = 12;

/**
 * Hook na dynamické výpočty veľkosti fontu pre sumu na základe dostupnej šírky
 * 
 * @param {string} cardId - Unikátny identifikátor karty
 * @param {string} textContent - Text sumy na vykreslenie (bez počtu – ten má fixnú veľkosť)
 * @returns {object} - { fontSize, containerRef }
 */
export function useResizingFontSize(cardId, textContent) {
  const [computedFontSize, setComputedFontSize] = useState(null);
  const [shouldRecalculate, setShouldRecalculate] = useState(0);
  const containerRef = useRef(null);
  const hiddenMeasureRef = useRef(null);
  const { minFontSize, registerFontSize } = useKpiCardFontSize();

  // Inicializuj skrytý element na meranie
  useEffect(() => {
    if (!hiddenMeasureRef.current) {
      const measure = document.createElement("span");
      measure.style.visibility = "hidden";
      measure.style.position = "absolute";
      measure.style.top = "0";
      measure.style.left = "-9999px";
      measure.style.whiteSpace = "nowrap";
      document.body.appendChild(measure);
      hiddenMeasureRef.current = measure;
    }

    return () => {
      // Cleanup: vymaž skrytý element pri unmounte
      if (hiddenMeasureRef.current) {
        hiddenMeasureRef.current.remove();
        hiddenMeasureRef.current = null;
      }
    };
  }, []);

  // Vypočítaj optimálnu veľkosť fontu (v useLayoutEffect - po renderi, pred paintom)
  useLayoutEffect(() => {
    if (!containerRef.current || !hiddenMeasureRef.current || !textContent) {
      return;
    }

    const calculateOptimalFontSize = () => {
      const containerWidth = containerRef.current.offsetWidth;
      if (containerWidth <= 0) return null;

      const measure = hiddenMeasureRef.current;
      // Meriame podľa .ovw__kpi-value – suma sa vykresľuje tučným rezom
      const valueEl =
        containerRef.current.querySelector(".ovw__kpi-value") || containerRef.current;
      const computedStyle = window.getComputedStyle(valueEl);

      measure.style.fontFamily = computedStyle.fontFamily;
      measure.style.fontWeight = computedStyle.fontWeight;
      measure.style.letterSpacing = computedStyle.letterSpacing;

      // Horný limit = dizajnová veľkosť zo SCSS (--kpi-value-max-font-size),
      // aby sa font mohol už len zmenšovať, nikdy zväčšiť nad dizajn
      measure.style.fontSize =
        computedStyle.getPropertyValue("--kpi-value-max-font-size").trim() || "24px";
      const maxFontSize = Math.round(parseFloat(window.getComputedStyle(measure).fontSize)) || 24;

      // Počet (napr. "(3 ks)") má vlastnú, fixnú veľkosť fontu – jeho šírka sa
      // pri zmenšovaní sumy nemení, takže ju len odpočítame z dostupného miesta
      const countEl = containerRef.current.querySelector(".ovw__kpi-count");
      const countWidth = countEl
        ? countEl.offsetWidth + (parseFloat(window.getComputedStyle(countEl).marginLeft) || 0)
        : 0;

      // containerRef je priamo riadok so sumou, čiže už je to dostupná šírka
      // (ikona, gap aj padding karty sú mimo neho) – necháme si 2px rezervu
      const availableWidth = containerWidth - countWidth - 2;

      // Binárne vyhľadávanie najväčšej veľkosti, ktorá sa ešte zmestí
      let low = MIN_FONT_SIZE;
      let high = Math.max(MIN_FONT_SIZE, maxFontSize);
      let optimalSize = MIN_FONT_SIZE;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        measure.style.fontSize = `${mid}px`;
        measure.textContent = textContent;

        if (measure.offsetWidth <= availableWidth) {
          optimalSize = mid;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      return optimalSize;
    };

    const newFontSize = calculateOptimalFontSize();
    if (newFontSize !== null && newFontSize !== computedFontSize) {
      setComputedFontSize(newFontSize);
      registerFontSize(cardId, newFontSize);
    }
  }, [textContent, cardId, shouldRecalculate]);

  // ResizeObserver – sleduj zmeny šírky containeru
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(() => {
      // Trigger recalculation durch zmenu shouldRecalculate
      setShouldRecalculate(prev => prev + 1);
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Vráť minimálnu veľkosť spomedzi všetkých kariet (ak existuje), inak vypočítanú
  const finalFontSize = useMemo(() => {
    return minFontSize !== null && minFontSize !== undefined ? minFontSize : computedFontSize;
  }, [minFontSize, computedFontSize]);

  return { fontSize: finalFontSize, containerRef };
}

/**
 * Komponent na vykresľovanie sumy s dynamickou veľkosťou fontu
 */
export function DynamicFontValue({
  cardId,
  value,
  count = "",
  colorClass,
  measureText,
}) {
  const { fontSize, containerRef } = useResizingFontSize(cardId, measureText);

  return (
    <div
      ref={containerRef}
      className="ovw__kpi-value-row"
      style={{
        // undefined => platí dizajnová veľkosť zo SCSS (žiadny preblik pred meraním)
        fontSize: fontSize ? `${fontSize}px` : undefined,
      }}
    >
      <span className={`ovw__kpi-value ${colorClass || ""}`}>
        {value}
      </span>
      {count && count.trim() && (
        <span className="ovw__kpi-count">
          {count}
        </span>
      )}
    </div>
  );
}
